// hooks/usePlaceholderDucts.js
import { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import { useCurrentTenant } from "./useCurrentTenant";
import { isEmptyId } from "../builders/dummyDucts";

/**
 * Which ducts of a scenario's settings fall back to placeholder ducts:
 * nothing selected (empty Guid) or the selected duct no longer exists.
 *
 *   const { enclosure, sections, any, loaded } = usePlaceholderDucts(scenario?.settings);
 *
 * Returns all false until the duct list is loaded, so no hint flashes up
 * while loading.
 */
export function usePlaceholderDucts(settings) {
  const tenantId = useCurrentTenant();
  const [ducts, setDucts] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/duct")
      .then((res) => { if (alive) setDucts(res.data || []); })
      .catch((err) => console.error("Failed to load ducts:", err));
    return () => { alive = false; };
  }, []);

  const enclosureId = settings?.ductsEnclosureId;
  const sectionsId = settings?.ductsSectionsId;

  return useMemo(() => {
    if (!settings || !ducts) {
      return { enclosure: false, sections: false, any: false, loaded: !!ducts };
    }

    const tenantDuctIds = new Set(
      ducts.filter((d) => d.tenantId === tenantId).map((d) => String(d.id))
    );
    const missing = (id) => isEmptyId(id) || !tenantDuctIds.has(String(id));

    const enclosure = missing(enclosureId);
    const sections = missing(sectionsId);
    return { enclosure, sections, any: enclosure || sections, loaded: true };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!settings, enclosureId, sectionsId, ducts, tenantId]);
}
